import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import {
  canManageOperacionesImportacion,
  getRoleFromToken,
} from "../../utils/roleAccess";
import { importacionOperacionesApi as api } from "../importacion-operaciones/importacionOperacionesApi";
import Daily from "./ConciliacionOperacionesDaily";
import MotorDePago from "./MotorDePago";
import AuditoriaFiserv from "./AuditoriaFiserv";
import AuditoriaClover from "./AuditoriaClover";
import CuotaACuota from "./CuotaACuota";
import "./conciliacion-operaciones-daily.css";
export default function ConciliacionOperaciones() {
  const nav = useNavigate(),
    { darkMode } = React.useContext(DarkModeContext),
    token = getToken(),
    role = useMemo(() => getRoleFromToken(token), [token]);
  const [fechaLiquidacion, setFechaLiquidacion] = useState(""),
    [archivoZip, setArchivoZip] = useState(null),
    [analizando, setAnalizando] = useState(false),
    [activeReconciliationRunId, setActiveReconciliationRunId] = useState(""),
    [activeRun, setActiveRun] = useState(null),
    [reports, setReports] = useState([]),
    [retryingFailedReports, setRetryingFailedReports] = useState(false),
    [matchingBusy, setMatchingBusy] = useState(false),
    [matchingPhase, setMatchingPhase] = useState(""),
    [historialConciliacion, setHistorialConciliacion] = useState(null),
    [cargandoHistorial, setCargandoHistorial] = useState(false),
    refreshInFlight = useRef(null),
    latestRunId = useRef(""),
    lastRunStatus = useRef(null),
    [abriendoRunId, setAbriendoRunId] = useState("");
  useEffect(() => {
    if (!token || !canManageOperacionesImportacion(role)) nav("/");
  }, [nav, role, token]);
  const history = useCallback(async () => {
    try {
      setCargandoHistorial(true);
      setHistorialConciliacion(await api.obtenerHistorialReconciliation(token));
    } catch (e) {
      setHistorialConciliacion({ runs: [], error: e.message });
    } finally {
      setCargandoHistorial(false);
    }
  }, [token]);
  useEffect(() => {
    if (token) history();
  }, [history, token]);
  // El guard de "refresh en curso" es por run: si está en vuelo el refresh de otro run, no se
  // reutiliza (antes devolvía la promesa del run viejo y abrir uno del histórico no hacía nada).
  // Y una respuesta que llega cuando el usuario ya cambió de run se descarta en vez de pisar.
  const refresh = useCallback(
    (id) => {
      latestRunId.current = id;
      if (refreshInFlight.current?.id === id) return refreshInFlight.current.promise;
      const promise = (async () => {
        try {
          const r = await api.obtenerReconciliationRun(id, token);
          const p = await api.obtenerReconciliationReports(id, token);
          if (latestRunId.current === id) {
            setActiveRun(r);
            setReports(p.reports || []);
            lastRunStatus.current = r.status;
          }
          const c = r.clover || {};
          if (
            +c.pending + +c.running + +c.complete + +c.empty + +c.failed !==
            +c.totalReports
          )
            console.warn("[CRM RECON COUNTER MISMATCH]", r);
          return r;
        } finally {
          if (refreshInFlight.current?.id === id) refreshInFlight.current = null;
        }
      })();
      refreshInFlight.current = { id, promise };
      return promise;
    },
    [token],
  );
  // Solo se sondea mientras Clover está descargando: en cualquier otro estado el run no cambia
  // sin una acción del usuario, y esas acciones ya llaman a refresh. Si una acción vuelve a
  // poner el run en CLOVER_RUNNING, el sondeo se reanuda solo.
  useEffect(() => {
    if (!activeReconciliationRunId) return;
    let live = true;
    lastRunStatus.current = null;
    const poll = async () => {
      try {
        await refresh(activeReconciliationRunId);
      } catch (e) {
        console.error("[CRM RECON POLL]", e);
      }
    };
    poll();
    const id = setInterval(() => {
      const running = lastRunStatus.current === null || lastRunStatus.current === "CLOVER_RUNNING";
      if (live && running && !refreshInFlight.current) poll();
    }, 3000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [activeReconciliationRunId, refresh]);
  const handleAnalizarZip = async (e) => {
    e.preventDefault();
    if (!archivoZip || !fechaLiquidacion)
      return Swal.fire(
        "Conciliación",
        "Seleccioná fecha y ZIP Fiserv.",
        "info",
      );
    try {
      setAnalizando(true);
      setActiveReconciliationRunId("");
      setActiveRun(null);
      setReports([]);
      const f = new FormData();
      f.append("archivo", archivoZip);
      f.append("fechaLiquidacion", fechaLiquidacion);
      f.append("token", token || "");
      const x = await api.crearReconciliationRun(f, token);
      setActiveReconciliationRunId(x.reconciliationRunId);
      console.info(
        "[CRM RECON ACTIVE RUN] reconciliationRunId=" + x.reconciliationRunId,
      );
      await api.iniciarReconciliationClover(x.reconciliationRunId, token);
      await refresh(x.reconciliationRunId);
    } catch (e) {
      Swal.fire("Conciliación", e.message || "No se pudo iniciar.", "error");
    } finally {
      setAnalizando(false);
    }
  };
  const retry = async () => {
    if (!activeReconciliationRunId) return;
    try {
      setRetryingFailedReports(true);
      await api.reintentarReconciliationFailed(
        activeReconciliationRunId,
        token,
      );
      await refresh(activeReconciliationRunId);
    } catch (e) {
      Swal.fire("Reintento", e.message, "error");
    } finally {
      setRetryingFailedReports(false);
    }
  };
  const match = async () => {
    if (!activeReconciliationRunId || matchingBusy) return;
    try {
      setMatchingBusy(true);
      setMatchingPhase("1. Cargando transacciones Clover");
      console.info("[UI MATCH REQUEST]", { runId: activeReconciliationRunId });
      const post = await api.ejecutarReconciliationMatch(
        activeReconciliationRunId,
        token,
      );
      console.info("[UI MATCH POST RESPONSE]", {
        runId: activeReconciliationRunId,
        status: 200,
        body: post,
      });
      setMatchingPhase("2. Preparando comparables");
      const get = await api.obtenerReconciliationMatch(
        activeReconciliationRunId,
        token,
      );
      const metrics = get?.metrics || {};
      setMatchingPhase("3. Ejecutando Matcher V2");
      console.info("[UI MATCH RESPONSE]", {
        runId: activeReconciliationRunId,
        matchStatus: get?.status || get?.mode,
        fiservTotal: metrics.fiservTotal,
        fiservComparable: metrics.fiservComparable,
        cloverTransactionsLoaded: metrics.cloverTotal,
        matched: metrics.match,
        review: (metrics.matchProbable || 0) + (metrics.conflicto || 0),
        missing: metrics.soloFiserv,
        soloClover: metrics.soloClover,
        matchRate: metrics.matchRateComparable,
        rows: get?.results?.length || 0,
      });
      setMatchingPhase("4. Guardando resultado");
      await refresh(activeReconciliationRunId);
    } catch (e) {
      console.error("[UI MATCH POST RESPONSE]", {
        runId: activeReconciliationRunId,
        status: e.status,
        body: e.body,
        error: e.message,
      });
      // Si el error fue de sesión, este refresh también falla: capturarlo evita que un segundo
      // rechazo escape del catch y tire la pantalla con "Uncaught runtime error".
      try { await refresh(activeReconciliationRunId); } catch (refreshError) { console.error("[UI MATCH REFRESH]", refreshError); }
      Swal.fire(
        "Validación",
        e.status === 401 || e.status === 403 ? "Tu sesión venció. Volvé a iniciar sesión." : e.message || "No se pudo ejecutar la validación de operaciones.",
        "error",
      );
    } finally {
      setMatchingBusy(false);
      setMatchingPhase("");
    }
  };
  const m =
    activeRun?.status === "MATCH_INPUT_ERROR" ? null : activeRun?.matcher;
  const resolveReview = async (result, resolutionType, cloverPaymentId = "", observation = "") => {
    if (!activeReconciliationRunId) return;
    const fiserv = result?.fiserv || {};
    const fiservOperationId = `${fiserv.sourceFile || ""}|${fiserv.sourceRow || 0}|${fiserv.numeroComercio || ""}|${String(fiserv.fechaOperacion || "").slice(0, 10)}|${fiserv.numeroCupon || ""}|${fiserv.numeroLote || ""}`;
    try {
      const response = await api.resolverReconciliationReview(activeReconciliationRunId, { fiservOperationId, cloverPaymentId, resolutionType, observation }, token);
      setActiveRun(response.run);
    } catch (error) { Swal.fire("Revisión manual", error.message || "No se pudo guardar la resolución.", "error"); }
  };
  const confirmTerminalMapping = async (item) => {
    const candidate = item?.cloverCandidate;
    const terminalFiserv = item?.terminalFiserv || item?.terminal || "";
    if (!activeReconciliationRunId || !terminalFiserv || !candidate?.merchantId || !candidate?.accountKey) {
      Swal.fire("Mapping", "Falta la terminal o el comercio Clover seleccionado.", "error");
      return;
    }
    const decision = await Swal.fire({ title: "¿Confirmar mapping?", text: `Terminal ${terminalFiserv} → ${candidate.commerceName || candidate.merchantId}. Se aplicará a todas las familias de esa terminal, sin iniciar Clover.`, icon: "warning", showCancelButton: true, confirmButtonText: "Confirmar", cancelButtonText: "Cancelar" });
    if (!decision.isConfirmed) return;
    try {
      const response = await api.confirmarReconciliationMapping(activeReconciliationRunId, { terminalFiserv, merchantId: candidate.merchantId, accountKey: candidate.accountKey, commerceName: candidate.commerceName || "" }, token);
      setActiveRun(response.run);
      Swal.fire("Mapping confirmado", `Terminal ${terminalFiserv}: se prepararon ${response.confirmation?.newReportNeeds?.length || 0} reportes Clover nuevos. No se inició ninguna descarga.`, "success");
    } catch (error) { Swal.fire("Mapping", error.message || "No se pudo confirmar el mapping.", "error"); }
  };
  const consultarCloverNuevosMappings = async () => {
    if (!activeReconciliationRunId) return;
    try {
      const response = await api.iniciarCloverNuevosMappings(activeReconciliationRunId, token);
      setActiveRun(response.run);
      Swal.fire("Clover", response.requested ? `Se solicitaron ${response.requested} reportes nuevos. Los existentes no se tocaron.` : response.mensaje || "No hay reportes nuevos para solicitar.", "success");
    } catch (error) { Swal.fire("Clover", error.message || "No se pudieron consultar los nuevos mappings.", "error"); }
  };
  const buscarCloverMerchants = async (query) => {
    const response = await api.buscarReconciliationMerchants(query, token);
    return response.items || [];
  };
  const recalcularDebit = async () => {
    if (!activeReconciliationRunId) return;
    setMatchingPhase("Calculando conciliación Débito: preparando Fiserv, cargando Clover persistido y guardando resultado...");
    setMatchingBusy(true);
    try { const response = await api.recalcularReconciliationDebit(activeReconciliationRunId, token); setActiveRun(response.run); }
    catch (error) { Swal.fire("Conciliación Débito", error.message || "No se pudo calcular Débito.", "error"); }
    finally { setMatchingBusy(false); setMatchingPhase(""); }
  };
  // Cerrar una fila no la concilia: registra que un humano la revisó. Por eso el motivo es
  // obligatorio en CONFIRMED_MISSING y queda guardado junto a la evidencia del momento.
  const resolverDebitRow = async (row, action) => {
    if (!activeReconciliationRunId || !row?.fiservOperationId) return;
    let reason = "";
    if (action === "CONFIRMED_MISSING") {
      const prompt = await Swal.fire({
        title: "Confirmar operación sin correspondencia",
        html: `Terminal <b>${row.terminal}</b> · ${row.coupon ? `cupón ${row.coupon} · ` : ""}causa detectada: <b>${row.missingReason || row.rule || "-"}</b><br/><small>Esto no cuenta como conciliada: queda cerrada como verificada sin correspondencia en Clover.</small>`,
        input: "text",
        inputLabel: "Motivo (obligatorio)",
        inputValidator: (value) => (!value || !value.trim() ? "El motivo es obligatorio." : undefined),
        showCancelButton: true,
        confirmButtonText: "Cerrar operación",
      });
      if (!prompt.isConfirmed) return;
      reason = prompt.value.trim();
    }
    try {
      await api.resolverReconciliationDebitRow(activeReconciliationRunId, token, { fiservOperationId: row.fiservOperationId, action, reason, cloverPaymentId: row.paymentId || "" });
      const run = await api.obtenerReconciliationRun(activeReconciliationRunId, token);
      setActiveRun(run);
    } catch (error) { Swal.fire("Conciliación Débito", error.message || "No se pudo registrar la resolución.", "error"); }
  };
  const simularDebitAllClover = async () => {
    if (!activeReconciliationRunId) return;
    setMatchingPhase("Simulando Débito contra todas las Clover persistidas...");
    setMatchingBusy(true);
    try {
      const result = await api.simularReconciliationDebitAllClover(activeReconciliationRunId, token);
      const simulation = result.simulation || {};
      await Swal.fire({ title: "Simulación Débito read-only", html: `<p>Fiserv Débito: <b>${result.fiservDebit}</b></p><p>Actual: <b>408 MATCH / 0 REVIEW / 349 MISSING</b></p><p>Simulación: <b>${simulation.match || 0} MATCH / ${simulation.review || 0} REVIEW / ${simulation.missing || 0} MISSING</b></p><p>Filtradas por metadata Debit que sí matchean: <b>${simulation.filteredByDebitClassifierWouldHaveMatched || 0}</b></p><p>Sin persistir cambios.</p>`, icon: "info" });
    } catch (error) { Swal.fire("Simulación Débito", error.message || "No se pudo ejecutar la simulación.", "error"); }
    finally { setMatchingBusy(false); setMatchingPhase(""); }
  };
  return (
    <>
    <Daily
      darkMode={darkMode}
      fechaLiquidacion={fechaLiquidacion}
      setFechaLiquidacion={setFechaLiquidacion}
      archivoZip={archivoZip}
      setArchivoZip={setArchivoZip}
      analizando={analizando}
      handleAnalizarZip={handleAnalizarZip}
      activeReconciliationRunId={activeReconciliationRunId}
      operationalRun={activeRun}
      reportProgress={{ ...(activeRun?.clover || {}), reports }}
      matchingBusy={matchingBusy}
      matchingPhase={matchingPhase}
      ejecutarMatch={match}
      resolverReview={resolveReview}
      confirmTerminalMapping={confirmTerminalMapping}
      consultarCloverNuevosMappings={consultarCloverNuevosMappings}
      buscarCloverMerchants={buscarCloverMerchants}
      recalcularDebit={recalcularDebit}
      resolverDebitRow={resolverDebitRow}
      simularDebitAllClover={simularDebitAllClover}
      retryingFailedReports={retryingFailedReports}
      retryFailedReports={retry}
      historialConciliacion={historialConciliacion}
      cargandoHistorial={cargandoHistorial}
      cargarHistorialConciliacion={history}
      abriendoRunId={abriendoRunId}
      abrirRunConciliacion={async (id) => {
        if (!id || abriendoRunId) return;
        setAbriendoRunId(id);
        setActiveRun(null);
        setReports([]);
        setActiveReconciliationRunId(id);
        try {
          await refresh(id);
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
          Swal.fire("Histórico", e.status === 401 || e.status === 403 ? "Tu sesión venció. Volvé a iniciar sesión." : e.message || "No se pudo abrir la conciliación.", "error");
        } finally {
          setAbriendoRunId("");
        }
      }}
      mappingSummary={
        activeRun
          ? {
              confirmed: activeRun.mappings?.confirmed,
              suggested: activeRun.mappings?.suggested,
              total: activeRun.mappings?.cloverNeeds,
              pending: activeRun.mappings?.pending,
              items: activeRun.mappings?.items || [],
            }
          : null
      }
      debit={activeRun?.debitReconciliation ? { ...activeRun.debit, goldenFiserv: activeRun.debitReconciliation.goldenCount, builderAvailable: activeRun.debitReconciliation.availableCount, mapped: activeRun.debitReconciliation.mappedCount, unmapped: activeRun.debitReconciliation.availableCount - activeRun.debitReconciliation.mappedCount, rows: activeRun.debitReconciliation.rows, missingByReason: activeRun.debitReconciliation.missingByReason, mappingHints: activeRun.debitReconciliation.mappingHints, closedCount: activeRun.debitReconciliation.closedCount, manualMatchCount: activeRun.debitReconciliation.manualMatchCount, confirmedMissingCount: activeRun.debitReconciliation.confirmedMissingCount, contradictedResolutionCount: activeRun.debitReconciliation.contradictedResolutionCount, cloverWithoutFiserv: activeRun.debitReconciliation.cloverUnusedRows, cloverWithoutFiservAmount: activeRun.debitReconciliation.cloverUnusedAmount, cloverConsumedCount: activeRun.debitReconciliation.cloverConsumedCount, generatedAt: activeRun.debitReconciliation.generatedAt, fechaLiquidacion: activeRun.fechaLiquidacion, calculated: true } : activeRun?.debit}
      openMappingModal={() => {}}
      mappingModal={null}
      setMappingModal={() => {}}
      mappingCandidates={[]}
      mappingBusy={false}
      saveMapping={() => {}}
      mappingQuery=""
      setMappingQuery={() => {}}
      searchMappingCandidates={() => {}}
      unlinkMapping={() => {}}
      resultadoConciliacion={
        m
          ? {
              resumen: {
                operacionesFiserv: activeRun.fiserv?.comparableRows,
                conciliadas: m.matched,
                autoMatched: m.autoMatched,
                manualMatched: m.manualMatched,
                matchProbable: m.review,
                soloFiserv: m.missing,
                explainedRate: m.explainedRate,
              },
              run: {
                id: activeReconciliationRunId,
                matchAutomaticoPorcentaje: m.autoMatchRate,
                totalMatchPorcentaje: m.matchRate,
              },
              resultados: m.results || [],
              families: m.families || [],
            }
          : null
      }
    />
    {/* Misma pantalla, bloques aparte. La auditoria Fiserv va primero: responde que
        trajo Fiserv, antes de cualquier cruce con Clover. */}
    <AuditoriaFiserv runId={activeReconciliationRunId} token={token} darkMode={darkMode} />
    <AuditoriaClover runId={activeReconciliationRunId} token={token} darkMode={darkMode} />
    <MotorDePago runId={activeReconciliationRunId} token={token} darkMode={darkMode} />
    {/* Simulacion aislada del esquema futuro: no alimenta el Motor de pago. */}
    <CuotaACuota runId={activeReconciliationRunId} token={token} darkMode={darkMode} />
    </>
  );
}
